// State of the AI assistant that must outlive the component: every route mounts its own
// MainLayout, so the panel unmounts on navigation. The conversation, the open state and the
// request in flight live here (read with useAiStore), the conversation also in sessionStorage.
// React
import { useSyncExternalStore } from "react";

// API
import { getAiStatus, sendAiChat } from "api/ai.jsx";

// Utils
import { getCurrentOperatorId, isClientUser } from "utils/auth.jsx";

// Same limits as the backend (back/src/apps/ai_assistant/constants.py)
export const MAX_HISTORY_MESSAGES = 20;
export const MAX_MESSAGE_CHARS = 4000;
// Messages kept in sessionStorage
const MAX_STORED_MESSAGES = 60;
const STORAGE_PREFIX = "aiAssistant:conversation:";

// Window events: the navbar button sends TOGGLE_EVENT; OPEN_EVENT and ENTITY_CHAT_OPEN_EVENT keep
// this panel and the comments chat (components/Chat) from being open together.
export const TOGGLE_EVENT = "ai-assistant:toggle";
export const OPEN_EVENT = "ai-assistant:open";
export const ENTITY_CHAT_OPEN_EVENT = "entity-chat:open";
// Full screen chat page: the floating button and the panel step aside there
export const AI_CHAT_PATH = "/chat";
// Suggestions shown under a reply (same limits as the backend)
const MAX_SUGGESTIONS = 3;
const MAX_SUGGESTION_CHARS = 120;

// ---------------------------------------------------------------------------------------------
// Status (enabled or not): asked once per page load. 404 = disabled, quietly. Other failures
// (network, 5xx) are retried on the next mount.

let statusPromise = null;

export const loadAiStatus = () => {
  if (isClientUser()) return Promise.resolve(false);
  if (!statusPromise) {
    statusPromise = getAiStatus()
      .then((response) => Boolean(response?.data?.enabled))
      .catch((error) => {
        if (error?.response?.status !== 404) statusPromise = null;
        return false;
      });
  }
  return statusPromise;
};

// ---------------------------------------------------------------------------------------------
// Conversation store

const storageKey = (ownerId) => `${STORAGE_PREFIX}${ownerId ?? "anonymous"}`;

const readMessages = (ownerId) => {
  try {
    const raw = sessionStorage.getItem(storageKey(ownerId));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? parsed
          .filter(
            (message) =>
              message &&
              ["user", "assistant", "error"].includes(message.role) &&
              typeof message.id === "string"
          )
          // sessionStorage outlives the bundle: same checks as a fresh reply
          .map((message) =>
            message.role === "assistant"
              ? {
                  ...message,
                  ui: uiBlocksOf(message.ui),
                  suggestions: suggestionsOf(message.suggestions),
                }
              : message
          )
      : [];
  } catch {
    return [];
  }
};

const writeMessages = (ownerId, messages) => {
  const kept = messages.slice(-MAX_STORED_MESSAGES);
  try {
    if (kept.length) sessionStorage.setItem(storageKey(ownerId), JSON.stringify(kept));
    else sessionStorage.removeItem(storageKey(ownerId));
  } catch {
    // Storage full (the blocks of many replies are heavy): keep the blocks of the last reply only
    try {
      const lastReply = kept.map((m) => m.role).lastIndexOf("assistant");
      const slim = kept.map((message, index) =>
        index === lastReply ? message : { ...message, ui: undefined }
      );
      sessionStorage.setItem(storageKey(ownerId), JSON.stringify(slim));
    } catch {
      // Blocked too: the conversation just lives until the next reload
    }
  }
};

let state = {
  ownerId: undefined, // operator the conversation belongs to (a new login starts empty)
  open: false,
  messages: [],
  pending: false,
  pendingSince: null,
};
let controller = null;
let nextId = 1;
const listeners = new Set();

const setState = (patch, { persist = false } = {}) => {
  state = { ...state, ...patch };
  if (persist) writeMessages(state.ownerId, state.messages);
  listeners.forEach((listener) => listener());
};

const newId = () => `${Date.now().toString(36)}-${nextId++}`;

/**
 * Logout: forgets every stored conversation (sessionStorage survives localStorage.clear()) and
 * the one in memory, so the next login starts empty even in the same tab.
 */
export const clearAiConversationStorage = () => {
  controller?.abort();
  controller = null;
  try {
    Object.keys(sessionStorage)
      .filter((key) => key.startsWith(STORAGE_PREFIX))
      .forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // Storage blocked: nothing was stored
  }
  setState({ ownerId: undefined, open: false, messages: [], pending: false, pendingSince: null });
};

// Loads the conversation of the logged operator (and drops the one of a previous login)
const ensureOwner = () => {
  const ownerId = getCurrentOperatorId();
  if (state.ownerId === ownerId) return;
  controller?.abort();
  controller = null;
  state = {
    ...state,
    ownerId,
    open: false,
    messages: readMessages(ownerId),
    pending: false,
    pendingSince: null,
  };
};

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const getSnapshot = () => {
  ensureOwner();
  return state;
};

export const useAiStore = () => useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

// ---------------------------------------------------------------------------------------------
// Errors: a code for the UI text (aiAssistant:errors.<code>), plus the server message when there
// is one (the backend writes it in Italian).

const errorOf = (error) => {
  const status = error?.response?.status;
  const serverMessage = error?.response?.data?.data?.error || error?.response?.data?.detail || "";
  let code = "generic";
  if (error?.code === "ECONNABORTED" || error?.code === "ETIMEDOUT") code = "timeout";
  else if (!error?.response) code = "network";
  else if (status === 429) code = "rateLimited";
  else if (status === 503) code = "unavailable";
  else if (status === 502) code = "failed";
  else if (status === 400) code = "badRequest";
  else if (status === 404) code = "disabled";
  return { code, serverMessage: typeof serverMessage === "string" ? serverMessage : "" };
};

// Generative UI of a reply: { ref: { tool, data } } (only well-formed entries are kept)
const uiBlocksOf = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return Object.fromEntries(
    Object.entries(value).filter(
      ([ref, block]) =>
        /^r\d+$/.test(ref) &&
        block &&
        typeof block === "object" &&
        typeof block.tool === "string" &&
        block.data &&
        typeof block.data === "object"
    )
  );
};

const suggestionsOf = (value) =>
  Array.isArray(value)
    ? value
        .filter((item) => typeof item === "string" && item.trim())
        .map((item) => item.trim().slice(0, MAX_SUGGESTION_CHARS))
        .slice(0, MAX_SUGGESTIONS)
    : [];

const isAbort = (error) => error?.code === "ERR_CANCELED" || error?.name === "CanceledError";

// ---------------------------------------------------------------------------------------------
// Actions

export const setAiOpen = (open) => {
  ensureOwner();
  if (state.open === open) return;
  setState({ open });
  if (open) window.dispatchEvent(new CustomEvent(OPEN_EVENT));
};

export const toggleAiOpen = () => setAiOpen(!state.open);

// The text turns of the conversation, as the backend wants them (local errors left out)
const historyOf = (messages) =>
  messages
    .filter((message) => message.role === "user" || message.role === "assistant")
    .map(({ role, content }) => ({ role, content: String(content ?? "") }))
    .filter((message) => message.content.trim())
    .slice(-MAX_HISTORY_MESSAGES);

const request = async (messages, context) => {
  controller?.abort();
  const current = new AbortController();
  controller = current;
  const ownerId = state.ownerId;
  setState({ messages, pending: true, pendingSince: Date.now() }, { persist: true });

  try {
    const response = await sendAiChat(
      { messages: historyOf(messages), context },
      { signal: current.signal }
    );
    if (controller !== current || state.ownerId !== ownerId) return;
    const data = response?.data || {};
    setState(
      {
        messages: [
          ...state.messages,
          {
            id: newId(),
            role: "assistant",
            content: String(data.reply ?? ""),
            toolsUsed: Array.isArray(data.tools_used) ? data.tools_used : [],
            stopReason: data.stop_reason || null,
            ui: uiBlocksOf(data.ui_blocks),
            suggestions: suggestionsOf(data.suggestions),
          },
        ],
        pending: false,
        pendingSince: null,
      },
      { persist: true }
    );
  } catch (error) {
    if (controller !== current || state.ownerId !== ownerId) return;
    if (isAbort(error)) {
      setState({ pending: false, pendingSince: null });
      return;
    }
    const { code, serverMessage } = errorOf(error);
    setState(
      {
        messages: [...state.messages, { id: newId(), role: "error", code, serverMessage }],
        pending: false,
        pendingSince: null,
      },
      { persist: true }
    );
  } finally {
    if (controller === current) controller = null;
  }
};

/**
 * Sends a question. context: { path, company_id, company_name, surface } ("panel" | "page").
 * Returns false when there is nothing to send or a reply is still on its way.
 */
export const sendAiMessage = (text, context) => {
  ensureOwner();
  const content = String(text ?? "")
    .trim()
    .slice(0, MAX_MESSAGE_CHARS);
  if (!content || state.pending) return false;
  // A failed turn (the error and the question left without a reply) is replaced by the new one
  const messages = state.messages.filter((message) => message.role !== "error");
  while (messages.length && messages[messages.length - 1].role === "user") messages.pop();
  request([...messages, { id: newId(), role: "user", content }], context);
  return true;
};

// Sends the last question again after an error
export const retryAiMessage = (context) => {
  ensureOwner();
  if (state.pending) return;
  const messages = state.messages.filter((message) => message.role !== "error");
  if (messages[messages.length - 1]?.role !== "user") return;
  request(messages, context);
};

/**
 * Stops the reply on its way. Returns the text of the question, taken back out of the
 * conversation so the panel can put it back in the field.
 */
export const stopAiMessage = () => {
  if (!state.pending) return "";
  controller?.abort();
  controller = null;
  const last = state.messages[state.messages.length - 1];
  const messages = last?.role === "user" ? state.messages.slice(0, -1) : state.messages;
  setState({ messages, pending: false, pendingSince: null }, { persist: true });
  return last?.role === "user" ? last.content : "";
};

export const resetAiConversation = () => {
  controller?.abort();
  controller = null;
  setState({ messages: [], pending: false, pendingSince: null }, { persist: true });
};
